import { CssBaseline, ThemeProvider } from '@mui/material';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createAppTheme } from '../theme';
import { BrandingConfig, brandingService, defaultBranding } from '../services/brandingService';
import { storageService } from '../services/storageService';

interface BrandingContextValue {
  branding: BrandingConfig;
  setBranding: (value: BrandingConfig) => void;
  refreshBranding: () => Promise<void>;
}

const cacheKey = 'tenant-branding';
const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

const readCachedBranding = (): BrandingConfig => {
  try {
    const value = localStorage.getItem(cacheKey);
    return value ? JSON.parse(value) : defaultBranding;
  } catch {
    localStorage.removeItem(cacheKey);
    return defaultBranding;
  }
};

export const BrandingProvider = ({ children }: { children: ReactNode }) => {
  const [branding, updateBranding] = useState<BrandingConfig>(readCachedBranding);

  const setBranding = useCallback((value: BrandingConfig) => {
    updateBranding(value);
    localStorage.setItem(cacheKey, JSON.stringify(value));
  }, []);

  const refreshBranding = useCallback(async () => {
    if (!storageService.getTokenData()) return;

    try {
      const response = await brandingService.get();
      if (response.data.success) setBranding(response.data.data);
    } catch (error) {
      console.error('Не удалось загрузить оформление организации', error);
    }
  }, [setBranding]);

  useEffect(() => {
    document.title = branding.productName;
  }, [branding.productName]);

  const theme = useMemo(() => createAppTheme(branding.primaryColor), [branding.primaryColor]);
  const value = useMemo(() => ({ branding, setBranding, refreshBranding }), [branding, setBranding, refreshBranding]);

  return (
    <BrandingContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) throw new Error('useBranding must be used inside BrandingProvider');
  return context;
};
